import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
} from "@mui/material";
import * as React from "react";

import type { FilterDefinition } from "@/shared/components/ResourceView";
import {
  CHART_DISPLAY_TYPES,
  ChartDisplayType,
  NumericAggregation,
  NUMERIC_AGGREGATIONS,
} from "@/shared/domain/metrics";

import {
  CardConfig,
  ChartCardConfig,
  RawChartCardConfig,
} from "../DashboardHeader/DashboardParams";

import styles from "./CardEditFields.module.css";
import { SearchSelect } from "./SearchSelect";

/** The option that clears a card's grouping.
 *
 *  Safe as a sentinel because every groupable value is an entity-prefixed wire
 *  path (`device.device_id`, `session.custom.<name>`) and so always contains a
 *  dot — a bare phrase cannot collide with one. Never sent anywhere. */
const NO_GROUPING = "No grouping";

/** The two things a chart card can plot. Not a persisted enum: the card
 *  itself says which it is by carrying an aggregation or not — this is only
 *  the picker's own vocabulary.
 *
 *  The labels name what happens to the SESSIONS in a period, which is the
 *  choice being made: collapse them into one value, or plot every one. */
const SERIES_OPTIONS = [
  { value: "aggregated", label: "Aggregated by period" },
  { value: "raw", label: "None (show all sessions)" },
] as const;

type SeriesKind = (typeof SERIES_OPTIONS)[number]["value"];

/** What a chart card falls back to when the user asks for an aggregated
 *  series and the panel has nothing remembered. Matches the defaults a freshly
 *  added chart card is created with. */
const DEFAULT_AGGREGATION: NumericAggregation = "mean";
const DEFAULT_DISPLAY: ChartDisplayType = "line";

export interface CardEditFieldsProps {
  /** The card being edited (controlled by the parent). */
  card: CardConfig;
  /** All selectable metric names. */
  allNames: string[];
  /** The metric to show selected — the card's own, or its resolved fallback
   *  when unset/stale (see effectiveTrendMetric / effectiveChartMetric). */
  effectiveMetric: string;
  /**
   * The fields an aggregated chart card may be split by, derived from the
   * dashboard's own filter menu (see `groupableFieldDefinitions`). Empty or
   * omitted — an org with no device rows and no discrete custom fields, or a
   * surface that does not offer grouping — hides the control entirely rather
   * than offering an empty picker.
   */
  groupableFields?: FilterDefinition[];
  /** Fired with the updated card whenever the user changes a picker. */
  onChange: (next: CardConfig) => void;
}

/**
 * A card's data pickers — metric, and then whatever else the card's kind and
 * series have to say for themselves.
 *
 * A chart card additionally chooses its SERIES: an aggregate per period
 * bucket, or every value the metric has. Those are mutually exclusive, and
 * the card type says so by having an aggregation or not — so the aggregation
 * and display pickers exist only on the aggregated branch rather than sitting
 * there inert on the other.
 *
 * Surface-agnostic: {@link CardEditPopover} hangs them off a card's ⋮ menu,
 * and the add-card flow slides them in behind its type picker.
 */
export function CardEditFields({
  card,
  allNames,
  effectiveMetric,
  groupableFields = [],
  onChange,
}: CardEditFieldsProps) {
  // What to restore if the user switches a chart back to Aggregated. This is
  // transient UI memory, not card data: a raw card genuinely has no
  // aggregation, so storing one on it to make the toggle feel nice would put
  // a value in the persisted definition that nothing reads.
  const [rememberedAggregation, setRememberedAggregation] =
    React.useState<NumericAggregation>(card.aggregation ?? DEFAULT_AGGREGATION);
  const [rememberedDisplay, setRememberedDisplay] =
    React.useState<ChartDisplayType>(
      (card.kind === "chart" ? card.display : undefined) ?? DEFAULT_DISPLAY,
    );
  // What to restore if a grouping the raw series cannot honour is dropped on
  // the way there and the user switches back. Unlike the aggregation and the
  // display, a grouping is NOT transient in general — both chart variants
  // carry one — so this remembers only what the card itself could not.
  const [rememberedGroupBy, setRememberedGroupBy] = React.useState<
    string | undefined
  >(card.kind === "chart" ? card.group_by : undefined);

  const isChart = card.kind === "chart";
  // Narrowed once, so the aggregation-dependent fields below are built from a
  // card the type system agrees HAS an aggregation — a trend tile or an
  // aggregated chart. `null` is exactly the raw chart card.
  const aggregatedCard = card.aggregation === undefined ? null : card;
  const isRawChart = aggregatedCard === null;

  /** What the Group by picker offers: clearing first, then the groupable
   *  fields in the order the dashboard sorted them. Both chart variants offer
   *  the same list — each resolves the field server-side, one to split its
   *  buckets and the other to tag its data points. */
  const groupByOptions = React.useMemo(
    () => [NO_GROUPING, ...groupableFields.map((d) => d.field)],
    [groupableFields],
  );

  /** Swap a chart card between its two variants. Builds the target variant
   *  explicitly rather than spreading and patching, so the card handed to
   *  `onChange` can never be a half-converted shape carrying both an
   *  aggregation and the absence that means "raw". */
  const changeSeries = (next: SeriesKind, chartCard: ChartCardConfig) => {
    const base: RawChartCardConfig = {
      id: chartCard.id,
      kind: "chart",
      metric: chartCard.metric,
      ...(chartCard.placement ? { placement: chartCard.placement } : {}),
      ...(chartCard.section ? { section: chartCard.section } : {}),
    };
    onChange(
      next === "raw"
        ? {
            ...base,
            // Grouping survives the swap in both directions: both variants
            // resolve the same fields server-side, so there is no grouping one
            // can honour and the other cannot.
            ...(rememberedGroupBy === undefined
              ? {}
              : { group_by: rememberedGroupBy }),
          }
        : {
            ...base,
            aggregation: rememberedAggregation,
            display: rememberedDisplay,
            ...(rememberedGroupBy === undefined
              ? {}
              : { group_by: rememberedGroupBy }),
          },
    );
  };

  /** Set (or, with `undefined`, remove) the card's group-by. Rebuilt rather
   *  than spread-and-patched for the clearing case: `{...card, group_by:
   *  undefined}` leaves the KEY in the object, which persists into the JSONB
   *  and reads back as a grouping nothing can resolve. */
  const changeGroupBy = (
    next: string | undefined,
    chartCard: ChartCardConfig,
  ) => {
    setRememberedGroupBy(next);
    const { group_by: _dropped, ...rest } = chartCard;
    onChange(next === undefined ? rest : { ...rest, group_by: next });
  };

  return (
    <div className={styles.editor}>
      <SearchSelect
        label="Metric"
        value={effectiveMetric}
        options={allNames}
        onChange={(next) => onChange({ ...card, metric: next })}
      />
      {isChart && (
        <FormControl size="small">
          <InputLabel id={`card-edit-series-${card.id}`}>
            Session Aggregation
          </InputLabel>
          <Select
            labelId={`card-edit-series-${card.id}`}
            label="Session Aggregation"
            value={isRawChart ? "raw" : "aggregated"}
            onChange={(e: SelectChangeEvent) =>
              changeSeries(e.target.value as SeriesKind, card)
            }
          >
            {SERIES_OPTIONS.map((s) => (
              <MenuItem key={s.value} value={s.value}>
                {s.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
      {aggregatedCard !== null && (
        <SearchSelect
          label="Aggregation"
          value={aggregatedCard.aggregation}
          options={[...NUMERIC_AGGREGATIONS]}
          onChange={(next) => {
            const aggregation = next as NumericAggregation;
            setRememberedAggregation(aggregation);
            onChange({ ...aggregatedCard, aggregation });
          }}
        />
      )}
      {aggregatedCard !== null && aggregatedCard.kind === "chart" && (
        <FormControl size="small">
          <InputLabel id={`card-edit-display-${card.id}`}>Display</InputLabel>
          <Select
            labelId={`card-edit-display-${card.id}`}
            label="Display"
            value={aggregatedCard.display}
            onChange={(e: SelectChangeEvent) => {
              const display = e.target.value as ChartDisplayType;
              setRememberedDisplay(display);
              onChange({ ...aggregatedCard, display });
            }}
          >
            {CHART_DISPLAY_TYPES.map((d) => (
              <MenuItem key={d.value} value={d.value}>
                {d.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
      {/* Both chart variants: grouping is a property of the SERIES, which
          both draw, not of the aggregation. They differ only in how many
          fields they can offer — the raw one splits its rows on the client,
          so it lists what a row can resolve — and the list going empty is
          what hides the control rather than offering a dead end. */}
      {isChart && groupByOptions.length > 1 && (
        <SearchSelect
          label="Group by"
          value={
            card.kind === "chart" ? card.group_by ?? NO_GROUPING : NO_GROUPING
          }
          // The input itself is the filter, as it is for Metric and
          // Aggregation: typing narrows the list in place rather than
          // opening a panel with a search box of its own.
          options={groupByOptions}
          onChange={(next) => {
            if (card.kind !== "chart") {
              return;
            }
            changeGroupBy(next === NO_GROUPING ? undefined : next, card);
          }}
        />
      )}
    </div>
  );
}
